import React, { useState, useEffect } from 'react';
import { Card, Form as BsForm, Button, ProgressBar, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import {
  SMS_JURIDICO_MAX_CHARS,
  SMS_JURIDICO_CHUNK_SIZE,
} from '~/constant';
import {
  listarGesUsuarioCobrancaSms,
  listarClientesCobrancaSms,
} from '~/Services/Service';
import {
  obterMensagem,
  salvarMensagem,
  excluirMensagem,
  listarCobradores,
  listarDestinatarios,
  dispararSms,
} from '~/Services/EnvioSmsJuridicoService';
import FiltrosDisparo from './components/FiltrosDisparo';
import ListaDestinatarios from './components/ListaDestinatarios';

const EnvioSms = () => {
  const [mensagem, setMensagem] = useState('');
  const [cobradores, setCobradores] = useState([]);
  const [ges, setGes] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [destinatarios, setDestinatarios] = useState([]);
  const [tipoDisparo, setTipoDisparo] = useState('PorContrato');

  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState(0);

  const [showMensagem, setShowMensagem] = useState(false);
  const [salvandoMensagem, setSalvandoMensagem] = useState(false);

  useEffect(() => {
    obterMensagem()
      .then((data) => setMensagem(data && data.texto ? data.texto : ''))
      .catch((err) => toast.error(err.message || 'Erro ao carregar mensagem'));

    listarCobradores()
      .then((data) => setCobradores(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.message || 'Erro ao carregar cobradores'));

    listarGesUsuarioCobrancaSms()
      .then((data) => setGes(Array.isArray(data) ? data : []))
      .catch(() => setGes([]));
  }, []);

  const handleGeChange = (codigoGrupoEmpresa) => {
    setClientes([]);
    if (!codigoGrupoEmpresa) return;
    listarClientesCobrancaSms(codigoGrupoEmpresa)
      .then((data) => setClientes(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.message || 'Erro ao carregar clientes'));
  };

  const handleCarregar = async (filtros) => {
    setCarregando(true);
    setDestinatarios([]);
    setTipoDisparo(filtros.tipo_disparo || 'PorContrato');
    try {
      const data = await listarDestinatarios(filtros);
      const lista = Array.isArray(data) ? data : [];
      setDestinatarios(lista);
      if (lista.length === 0) toast.info('Nenhum destinatário encontrado.');
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar destinatários');
    } finally {
      setCarregando(false);
    }
  };

  const handleLimpar = () => {
    setDestinatarios([]);
  };

  const handleRemover = (chave) => {
    const porContrato = tipoDisparo === 'PorContrato';
    setDestinatarios((prev) =>
      prev.filter((d) =>
        porContrato ? `c-${d.codigo_boleto}` !== chave : `p-${d.cpf}` !== chave
      )
    );
  };

  const handleSalvarMensagem = async () => {
    if (!mensagem || !mensagem.trim()) {
      toast.error('Mensagem obrigatória');
      return;
    }
    if (mensagem.length > SMS_JURIDICO_MAX_CHARS) {
      toast.error(`Máximo ${SMS_JURIDICO_MAX_CHARS} caracteres`);
      return;
    }
    setSalvandoMensagem(true);
    try {
      await salvarMensagem(mensagem);
      toast.success('Mensagem salva');
      setShowMensagem(false);
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar mensagem');
    } finally {
      setSalvandoMensagem(false);
    }
  };

  const handleExcluirMensagem = async () => {
    setSalvandoMensagem(true);
    try {
      await excluirMensagem();
      setMensagem('');
      toast.success('Mensagem excluída');
      setShowMensagem(false);
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir mensagem');
    } finally {
      setSalvandoMensagem(false);
    }
  };

  const handleDisparar = async () => {
    if (destinatarios.length === 0) {
      toast.error('Nenhum destinatário para disparo');
      return;
    }
    if (!mensagem || !mensagem.trim()) {
      toast.error('Configure a mensagem antes de disparar');
      return;
    }

    setEnviando(true);
    setProgresso(0);
    try {
      // Chunking 50/50 com indicação de progresso para volume alto.
      let enviados = 0;
      let falhas = 0;
      const total = destinatarios.length;

      for (let i = 0; i < total; i += SMS_JURIDICO_CHUNK_SIZE) {
        const chunk = destinatarios.slice(i, i + SMS_JURIDICO_CHUNK_SIZE);
        // eslint-disable-next-line no-await-in-loop
        const res = await dispararSms({
          destinatarios: chunk,
          texto_mensagem: mensagem,
          tipo_disparo: tipoDisparo,
        });
        if (res) {
          enviados += res.enviados || 0;
          falhas += res.falhas || 0;
        }
        setProgresso(Math.round((Math.min(i + chunk.length, total) / total) * 100));
      }

      if (falhas > 0) {
        toast.warning(`${enviados} enviados, ${falhas} falhas`);
      } else {
        toast.success(`${enviados} enviados, ${falhas} falhas`);
      }
      // Recarrega a lista (destinatários enviados saem pela anti-duplicata 9 dias).
      setDestinatarios([]);
    } catch (err) {
      toast.error(err.message || 'Não foi possível processar o envio.');
    } finally {
      setEnviando(false);
      setProgresso(0);
    }
  };

  const charsRestantes = SMS_JURIDICO_MAX_CHARS - (mensagem ? mensagem.length : 0);

  return (
    <div className="container-fluid px-3 py-3">
      <Header title="Envio de SMS (Jurídico)" />

      <Card className="shadow-sm mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <strong>Filtros</strong>
        </Card.Header>
        <Card.Body className="py-2">
          <FiltrosDisparo
            cobradores={cobradores}
            ges={ges}
            clientes={clientes}
            onCarregar={handleCarregar}
            onLimpar={handleLimpar}
            onConfigurarMensagem={() => setShowMensagem(true)}
            onGeChange={handleGeChange}
            loading={carregando}
          />
        </Card.Body>
      </Card>

      {enviando && (
        <ProgressBar
          animated
          now={progresso}
          label={`Enviando SMS... ${progresso}%`}
          className="mb-3"
        />
      )}

      {destinatarios.length > 0 && (
        <Card className="shadow-sm mb-3">
          <Card.Header
            style={{ backgroundColor: '#0747a6', color: '#fff' }}
            className="d-flex justify-content-between align-items-center"
          >
            <strong>Destinatários — {destinatarios.length}</strong>
            <Button
              variant="light"
              size="sm"
              onClick={handleDisparar}
              disabled={enviando}
            >
              Enviar SMS
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            <ListaDestinatarios
              destinatarios={destinatarios}
              tipoDisparo={tipoDisparo}
              onRemover={handleRemover}
            />
          </Card.Body>
        </Card>
      )}

      <Modal show={showMensagem} onHide={() => setShowMensagem(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Mensagem Padrão Jurídico</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BsForm.Group>
            <BsForm.Label>Texto da mensagem (máx. {SMS_JURIDICO_MAX_CHARS} caracteres)</BsForm.Label>
            <BsForm.Control
              as="textarea"
              rows={4}
              maxLength={SMS_JURIDICO_MAX_CHARS}
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder="Digite a mensagem de SMS jurídico..."
            />
            <BsForm.Text className={charsRestantes < 0 ? 'text-danger' : 'text-muted'}>
              {charsRestantes} caracteres restantes
            </BsForm.Text>
          </BsForm.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={handleExcluirMensagem}
            disabled={salvandoMensagem}
          >
            Excluir
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowMensagem(false)}
            disabled={salvandoMensagem}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSalvarMensagem}
            disabled={salvandoMensagem}
          >
            {salvandoMensagem ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EnvioSms;
